import type { z } from 'zod';

import { ApiError } from './api-error';
import { CSRF_HEADER, readCsrfToken } from './csrf';
import { env } from './env';

interface RequestOptions {
  /** Bypasses the API base path — used for the unprefixed probes like /health. */
  absolutePath?: boolean;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

interface MutationOptions extends RequestOptions {
  body?: unknown;
}

function buildUrl(path: string, options: RequestOptions): string {
  const base = options.absolutePath ? '' : env.VITE_API_BASE_URL.replace(/\/$/, '');
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  if (!options.query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(options.query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function buildHeaders(method: string, hasBody: boolean): Record<string, string> {
  const headers: Record<string, string> = {};

  if (hasBody) headers['content-type'] = 'application/json';

  // Every state-changing call must echo the CSRF cookie back as a header, or
  // the API's double-submit guard rejects it.
  if (!SAFE_METHODS.has(method)) {
    const token = readCsrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }

  return headers;
}

/**
 * Paths where a 401 is the answer, not a stale session.
 *
 * Signing in with the wrong password is a 401 that means "wrong password";
 * trying to refresh in response would be nonsense at best, and on /auth/refresh
 * itself it would recurse.
 */
const NO_REFRESH_ON_401 = new Set([
  '/auth/refresh',
  '/auth/login',
  '/auth/register',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
]);

/**
 * The one refresh in flight, shared by every caller that needs it.
 *
 * This is a correctness requirement, not a tidiness one. The API rotates the
 * refresh token and treats a second presentation of an already-rotated token as
 * theft: it revokes the entire login family. A feed page that fires five
 * requests as the access token expires would, without this, send five refreshes
 * with the same token — the first succeeds and the other four look exactly like
 * a stolen token being replayed, logging the person out for good.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh', {}), {
        method: 'POST',
        credentials: 'include',
        headers: buildHeaders('POST', false),
      });
      return response.ok;
    } catch {
      // Offline, or the request was cut off. Not a session problem, but there
      // is nothing to retry with either.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function request<T>(
  method: string,
  path: string,
  schema: z.ZodType<T>,
  options: MutationOptions = {},
  allowRefresh = true,
): Promise<T> {
  const hasBody = options.body !== undefined;

  const response = await fetch(buildUrl(path, options), {
    method,
    // Auth is cookie-based (spec §5), so every call must carry credentials.
    credentials: 'include',
    headers: buildHeaders(method, hasBody),
    body: hasBody ? JSON.stringify(options.body) : undefined,
    ...(options.signal ? { signal: options.signal } : {}),
  });

  // The access token lives fifteen minutes and the refresh token thirty days.
  // Without this, the fifteen minutes was the whole session: the browser held a
  // perfectly good refresh cookie that nothing ever presented, and reading a
  // long thread was enough to be silently signed out.
  if (response.status === 401 && allowRefresh && !NO_REFRESH_ON_401.has(path)) {
    if (await refreshSession()) {
      return request(method, path, schema, options, false);
    }
  }

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  const payload: unknown = response.status === 204 ? null : await response.json();

  // Runtime validation is web-owned (spec §3.1): contract drift surfaces here,
  // loudly, instead of as an undefined field three components deep.
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(response.status, [
      `Response from ${method} ${path} did not match the expected shape: ${parsed.error.issues
        .map((issue) => `${issue.path.join('.') || '(root)'} ${issue.message}`)
        .join('; ')}`,
    ]);
  }

  return parsed.data;
}

export const apiClient = {
  get: <T>(path: string, schema: z.ZodType<T>, options?: RequestOptions): Promise<T> =>
    request('GET', path, schema, options),
  post: <T>(path: string, schema: z.ZodType<T>, options?: MutationOptions): Promise<T> =>
    request('POST', path, schema, options),
  patch: <T>(path: string, schema: z.ZodType<T>, options?: MutationOptions): Promise<T> =>
    request('PATCH', path, schema, options),
  delete: <T>(path: string, schema: z.ZodType<T>, options?: MutationOptions): Promise<T> =>
    request('DELETE', path, schema, options),
};
